/**
 * Owner Dashboard display helpers — linked inquiry/listing highlight, queue lifecycle.
 */

import { getInquiryRecordId } from './bookingInquiryDisplay';

export const OWNER_LINKED_ACTIVE_CARD_CLASS =
  'ring-2 ring-signal-blue/60 shadow-md shadow-signal-blue/15 border-signal-blue/50 bg-signal-blue/[0.04] transition-all duration-300';

export const OWNER_INCOMING_QUEUE_CONTAINER_CLASS =
  'max-h-[min(70vh,36rem)] overflow-y-auto pr-1 space-y-3';

export const OWNER_INCOMING_INQUIRY_DETAIL_TEST_ID = 'owner-incoming-inquiry-detail';

export function getOwnerInquiryRecordId(inquiry) {
  return getInquiryRecordId(inquiry);
}

export function normalizeEntityId(value) {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  if (typeof value === 'object') {
    return normalizeEntityId(value.id ?? value._id ?? value.listing_id ?? value.listingId);
  }
  return '';
}

export function getInquiryListingId(inquiry) {
  if (!inquiry || typeof inquiry !== 'object') {
    return '';
  }
  return normalizeEntityId(
    inquiry.listing_id ?? inquiry.listingId ?? inquiry.listing,
  );
}

export function getListingId(listing) {
  if (!listing || typeof listing !== 'object') {
    return '';
  }
  return normalizeEntityId(listing.id ?? listing._id ?? listing.listing_id);
}

export function normalizeInquiryStatus(inquiry) {
  return String(inquiry?.status || '').trim().toLowerCase();
}

/** Pending inquiries that still need approve/decline in the incoming decision queue. */
export function isIncomingDecisionInquiry(inquiry) {
  return normalizeInquiryStatus(inquiry) === 'pending';
}

export function isApprovedFollowUpInquiry(inquiry) {
  return normalizeInquiryStatus(inquiry) === 'approved';
}

export function isResolvedDeclinedInquiry(inquiry) {
  return normalizeInquiryStatus(inquiry) === 'declined';
}

export function filterIncomingDecisionQueue(inquiries) {
  if (!Array.isArray(inquiries)) {
    return [];
  }
  return inquiries.filter(isIncomingDecisionInquiry);
}

export function filterApprovedFollowUpInquiries(inquiries) {
  if (!Array.isArray(inquiries)) {
    return [];
  }
  return inquiries.filter(isApprovedFollowUpInquiry);
}

export function filterDeclinedInquiries(inquiries) {
  if (!Array.isArray(inquiries)) {
    return [];
  }
  return inquiries.filter(isResolvedDeclinedInquiry);
}

export function compareInquiryOldestFirst(a, b) {
  const aTs = new Date(a?.created_at || 0).getTime();
  const bTs = new Date(b?.created_at || 0).getTime();
  const aValid = !Number.isNaN(aTs);
  const bValid = !Number.isNaN(bTs);
  if (!aValid && !bValid) {
    return 0;
  }
  if (!aValid) {
    return 1;
  }
  if (!bValid) {
    return -1;
  }
  return aTs - bTs;
}

/** Action queue: pending only, oldest first (most actionable). */
export function sortIncomingDecisionQueue(inquiries) {
  return [...filterIncomingDecisionQueue(inquiries)].sort(compareInquiryOldestFirst);
}

export function isListingLinkedToActiveInquiry(listingId, activeListingId) {
  const listing = normalizeEntityId(listingId);
  const active = normalizeEntityId(activeListingId);
  return Boolean(listing && active && listing === active);
}

export function sortListingsForActiveInquiry(listings, activeListingId) {
  if (!Array.isArray(listings) || listings.length === 0) {
    return [];
  }
  const activeId = normalizeEntityId(activeListingId);
  if (!activeId) {
    return [...listings];
  }

  const matched = [];
  const remainder = [];
  listings.forEach((listing) => {
    if (getListingId(listing) === activeId) {
      matched.push(listing);
    } else {
      remainder.push(listing);
    }
  });
  return [...matched, ...remainder];
}

export function getLinkedActiveCardClass(isActive, extraClass = '') {
  const base = isActive ? OWNER_LINKED_ACTIVE_CARD_CLASS : '';
  return [base, extraClass].filter(Boolean).join(' ').trim();
}

export function resolveDefaultActiveInquiryId(inquiries, isEligible = () => true) {
  if (!Array.isArray(inquiries)) {
    return null;
  }
  const eligible = inquiries.filter(isEligible);
  if (eligible.length === 0) {
    return null;
  }
  if (eligible.every(isIncomingDecisionInquiry)) {
    return getOwnerInquiryRecordId(sortIncomingDecisionQueue(eligible)[0]) || null;
  }
  return getOwnerInquiryRecordId(eligible[0]) || null;
}

export function pickNextActiveInquiryId(inquiries, { excludeId } = {}) {
  const queue = sortIncomingDecisionQueue(inquiries).filter((inquiry) => {
    const recordId = getOwnerInquiryRecordId(inquiry);
    return recordId && recordId !== excludeId;
  });
  return getOwnerInquiryRecordId(queue[0]) || null;
}

export function findActionableInquiryIdForListing(inquiries, listingId) {
  const normalizedListingId = normalizeEntityId(listingId);
  if (!normalizedListingId) {
    return null;
  }
  const match = sortIncomingDecisionQueue(inquiries).find(
    (inquiry) => getInquiryListingId(inquiry) === normalizedListingId,
  );
  return getOwnerInquiryRecordId(match) || null;
}

export function findInquiryByRecordId(inquiries, recordId) {
  if (!recordId || !Array.isArray(inquiries)) {
    return null;
  }
  return (
    inquiries.find((item) => getOwnerInquiryRecordId(item) === recordId) || null
  );
}

export function getActiveIncomingDecisionInquiry(inquiries, activeInquiryId) {
  const inquiry = findInquiryByRecordId(inquiries, activeInquiryId);
  return inquiry && isIncomingDecisionInquiry(inquiry) ? inquiry : null;
}

export function resolveActiveListingId(inquiries, activeInquiryId) {
  if (!activeInquiryId || !Array.isArray(inquiries)) {
    return '';
  }
  return getInquiryListingId(findInquiryByRecordId(inquiries, activeInquiryId));
}

export function applyInquiryStatusUpdate(inquiries, inquiryId, patch) {
  if (!Array.isArray(inquiries)) {
    return [];
  }
  return inquiries.map((inquiry) =>
    (getOwnerInquiryRecordId(inquiry) === inquiryId ? { ...inquiry, ...patch } : inquiry),
  );
}

/**
 * Owner Review belongs post-viewing (after viewing is completed).
 * TODO: wire full post-viewing review workflow in a follow-up PR.
 */
export function shouldShowOwnerReviewWorkspace(inquiry) {
  const state = String(inquiry?.viewing_state || '').toLowerCase();
  return state === 'viewing_confirmed';
}

export function ownerLinkedListingTestId(listingId) {
  return `owner-linked-listing-${listingId}`;
}
